import { LitElement, html, PropertyValues } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../../../utils/translator.helper';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { Subscription } from 'rxjs';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { GzTextareaWebComponent } from '../../../../../ui/gzInput';
import { GzRadioGroupWebComponent } from '../../../../../ui/gzRadio';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../../ui/gzForm/validators/max-length.validator';
import { RegexValidator } from '../../../../../ui/gzForm/validators/regex.validator';
import { NetworkPopUpEvents } from '../../NetworkBasePopUp';
import { FormValidator } from '../../../../../ui/gzForm/formField/types/form-validator';
import { NetworkFeedbackFormData } from '../helpers/types';
import { AccessibilitySelectorValues, ActionsSelectorValues, TreeViewSelectorValues } from '../helpers/contants';
import { LinkColor, LinkType } from '../../../../../ui/gzLinkComponent/types';
import { GzTextWebComponent } from '../../../../../ui/gzText';

/**
 * @summary This component is used for rendering the content of network feedback form
 * @tag gz-network-feedback-form
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-network-feedback-form')
export class GzNetworkFeedbackFormWebComponent extends LitElement {
  @state()
  public formGroup: FormGroup;

  @state()
  private subscription: Subscription | null;

  // general
  @query('gz-form-field#generalFeedback')
  private generalFeedback: GzFormFieldWebComponent<GzTextareaWebComponent, string>;

  // tree view
  @query('gz-form-field#treeViewFeedback')
  private treeViewFeedback: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  @query('gz-form-field#treeViewFeedbackText')
  private treeViewFeedbackText: GzFormFieldWebComponent<GzTextareaWebComponent, string>;

  // filters and smart views
  @query('gz-form-field#filtersFeedbackOne')
  private filtersFeedbackOne: GzFormFieldWebComponent<GzTextareaWebComponent, string>;

  @query('gz-form-field#filtersFeedbackTwo')
  private filtersFeedbackTwo: GzFormFieldWebComponent<GzTextareaWebComponent, string>;

  // actions
  @query('gz-form-field#actionsFeedbackOne')
  private actionsFeedbackOne: GzFormFieldWebComponent<GzTextareaWebComponent, string>;

  @query('gz-form-field#actionsFeedbackTwo')
  private actionsFeedbackTwo: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  // display and accessibility
  @query('gz-form-field#accessibilityFeedbackOne')
  private accessibilityFeedbackOne: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  @query('gz-form-field#accessibilityFeedbackOneText')
  private accessibilityFeedbackOneText: GzFormFieldWebComponent<GzTextareaWebComponent, string>;

  @query('gz-form-field#accessibilityFeedbackTwo')
  private accessibilityFeedbackTwo: GzFormFieldWebComponent<GzTextareaWebComponent, string>;

  @query('#details-text')
  private detailsTextElement: GzTextWebComponent;

  private readonly TEXTAREA_MAX_LENGTH = 500;

  private validators: FormValidator[] = [
    MaxLengthValidator(
      this.TEXTAREA_MAX_LENGTH,
      translateFn(`You have reached the limit of {maxLength} characters.`, { maxLength: this.TEXTAREA_MAX_LENGTH })
    ),
    RegexValidator(
      RegExp('^[a-zA-Z0-9,.;?!"`\':\\s\\r\\n-]+$'),
      translateFn(`Your feedback contains unsupported characters.`)
    ),
  ];

  static get styles() {
    return styles;
  }

  private buildFormGroup(): void {
    this.formGroup = FormGroup.build({
      generalFeedback: this.generalFeedback,
      treeViewFeedback: this.treeViewFeedback,
      treeViewFeedbackText: this.treeViewFeedbackText,
      filtersFeedbackOne: this.filtersFeedbackOne,
      filtersFeedbackTwo: this.filtersFeedbackTwo,
      actionsFeedbackOne: this.actionsFeedbackOne,
      actionsFeedbackTwo: this.actionsFeedbackTwo,
      accessibilityFeedbackOne: this.accessibilityFeedbackOne,
      accessibilityFeedbackOneText: this.accessibilityFeedbackOneText,
      accessibilityFeedbackTwo: this.accessibilityFeedbackTwo,
    });

    this.subscription = this.formGroup.valid$.subscribe((valid: boolean) => {
      this.dispatchEvent(
        new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
          bubbles: true,
          composed: true,
          detail: { value: !valid },
        })
      );
    });
  }

  public unsubscribeForm(): void {
    this.subscription?.unsubscribe();
    this.formGroup.unsubscribe();
  }

  private setFormValidators(): void {
    this.generalFeedback.Validators = [
      ...this.validators.concat(RequiredValidator(translateFn(`This field is required.`))),
    ];

    [
      this.treeViewFeedbackText,
      this.filtersFeedbackOne,
      this.filtersFeedbackTwo,
      this.actionsFeedbackOne,
      this.accessibilityFeedbackOneText,
      this.accessibilityFeedbackTwo,
    ].forEach(field => {
      field.Validators = [...this.validators];
    });
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.buildFormGroup();
    this.setFormValidators();
    this.setTranslatedStrings();
  }

  private setTranslatedStrings(): void {
    const articleLink: string = Application.Main.company.isPartner()
      ? 'https://www.bitdefender.com/business/support/en/77211-155130-network-inventory.html'
      : 'https://www.bitdefender.com/business/support/en/77209-155130-network-inventory.html';

    const detailsText = translateFn(
      'For details, refer to {OPEN_LINK_TAG} {OPEN_SPAN_TAG} this article. {CLOSE_SPAN_TAG} {CLOSE_LINK_TAG}',
      {
        OPEN_LINK_TAG: `<gz-link fontSize="12" href="${articleLink}" color="${LinkColor.PRIMARY}" type="${LinkType.UNDERLINE}">`,
        CLOSE_LINK_TAG: '</gz-link>',
        OPEN_SPAN_TAG: '<span>',
        CLOSE_SPAN_TAG: '</span>',
      }
    );

    this.detailsTextElement.innerHTML = detailsText;
  }

  disconnectedCallback() {
    this.unsubscribeForm();
    super.disconnectedCallback();
  }

  public getFormValue(): NetworkFeedbackFormData {
    return this.formGroup.value as NetworkFeedbackFormData;
  }

  render() {
    return html`
      <gz-text fontSize="14" class="mb-2">
        ${translateFn(
          'Shape a better experience by sharing your thoughts on the new Network page. Your feedback can make a difference!'
        )}
      </gz-text>

      <div class="d-flex mb-4">
        <gz-text id="details-text" fontSize="14" class="link-prefix"> </gz-text>
      </div>

      <div class="feedback-form-wrapper">
        <gz-title fontSize="16" fontWeight="500" class="title-container mb-2 title"
          >${translateFn('General feedback*')}</gz-title
        >
        <gz-form-field
          label="${translateFn('Your thoughts*')}"
          class="general feedback-field mb-4"
          id="generalFeedback"
        >
          <gz-textarea
            gz-form-input
            rows="4"
            maxChars="${this.TEXTAREA_MAX_LENGTH}"
            placeholder="${translateFn('Type here')}"
          ></gz-textarea>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-title fontSize="16" fontWeight="500" class="title-container mb-3 title">
          ${translateFn('Optional feedback')}
        </gz-title>

        <gz-title fontSize="11" fontWeight="600" class="title-container mb-2 text-uppercase subtitle">
          ${translateFn('Tree view')}
        </gz-title>

        <gz-text fontSize="14" class="mb-1">
          ${translateFn('1. What do you feel is the greatest benefit of the new tree view?')}
        </gz-text>

        <gz-form-field id="treeViewFeedback" class="tree-view feedback-field mb-1">
          <gz-radio-group
            gz-form-input
            class="d-flex flex-column"
            @radioSelectionChanged="${e => {
              this.treeViewFeedbackText.Input.setEnabled(e.detail.selectedValue === '3');
            }}"
          >
            <gz-radio
              value="${TreeViewSelectorValues.OrganizationalStructureActivities}"
              label="${translateFn('Visualize, maintain, and make changes to your organizational structure')}"
            ></gz-radio>
            <gz-radio
              value="${TreeViewSelectorValues.NavigationActivities}"
              label="${translateFn('Navigate to a certain location')}"
            ></gz-radio>
            <gz-radio
              value="${TreeViewSelectorValues.Other}"
              label="${translateFn('Other. Please specify:')}"
            ></gz-radio>
          </gz-radio-group>
        </gz-form-field>

        <gz-form-field class="tree-view feedback-field mb-3 -mt-2" id="treeViewFeedbackText" disabled>
          <gz-textarea
            gz-form-input
            rows="4"
            maxChars="${this.TEXTAREA_MAX_LENGTH}"
            placeholder="${translateFn('Type here')}"
          ></gz-textarea>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-title fontSize="11" fontWeight="600" class="title-container mb-2 text-uppercase subtitle">
          ${translateFn('Filters and smart views ')}
        </gz-title>

        <gz-text fontSize="14" class="mb-1">
          ${translateFn(
            '2. When looking for data in the Network page, what filter categories and default smart views do you find most useful? Do you need additional categories?'
          )}
        </gz-text>

        <gz-form-field class="smart-view feedback-field mb-3" id="filtersFeedbackOne">
          <gz-textarea
            gz-form-input
            rows="4"
            maxChars="${this.TEXTAREA_MAX_LENGTH}"
            placeholder="${translateFn('Type here')}"
          ></gz-textarea>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-text fontSize="14" class="mb-1">
          ${translateFn(
            '3. What kind of information do you typically look for in the Network page? Can you easily find the data with the available options?'
          )}
        </gz-text>

        <gz-form-field class="smart-view feedback-field mb-3" id="filtersFeedbackTwo">
          <gz-textarea
            gz-form-input
            rows="4"
            maxChars="${this.TEXTAREA_MAX_LENGTH}"
            placeholder="${translateFn('Type here')}"
          ></gz-textarea>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-title fontSize="11" fontWeight="600" class="title-container mb-2 text-uppercase subtitle">
          ${translateFn('Actions')}
        </gz-title>

        <gz-text fontSize="14" class="mb-1">
          ${translateFn('4. What actions do you use most frequently? Is the list of actions accessible?')}
        </gz-text>

        <gz-form-field class="actions feedback-field mb-2" id="actionsFeedbackOne">
          <gz-textarea
            gz-form-input
            rows="4"
            maxChars="${this.TEXTAREA_MAX_LENGTH}"
            placeholder="${translateFn('Type here')}"
          ></gz-textarea>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-text fontSize="14" class="mb-1"> ${translateFn('5. How do you access the list of actions?')} </gz-text>

        <gz-form-field id="actionsFeedbackTwo" class="actions feedback-field">
          <gz-radio-group gz-form-input class="d-flex flex-column">
            <gz-radio
              value="${ActionsSelectorValues.MenuAboveGrid}"
              label="${translateFn('From the menu above the Network grid')}"
            ></gz-radio>
            <gz-radio
              value="${ActionsSelectorValues.ContextualMenu}"
              label="${translateFn('From the contextual menu (right mouse button)')}"
            ></gz-radio>
          </gz-radio-group>
        </gz-form-field>

        <gz-title fontSize="11" fontWeight="600" class="title-container mb-2 text-uppercase subtitle">
          ${translateFn('Display and accessibility')}
        </gz-title>

        <gz-text fontSize="14" class="mb-1">
          ${translateFn(
            '6. When opening the Network page, what is the most important thing you want to quickly access?'
          )}
        </gz-text>

        <gz-form-field id="accessibilityFeedbackOne" class="accessibility feedback-field">
          <gz-radio-group
            gz-form-input
            class="d-flex flex-column"
            @radioSelectionChanged="${e => {
              this.accessibilityFeedbackOneText.Input.setEnabled(e.detail.selectedValue === '7');
            }}"
          >
            <gz-radio
              value="${AccessibilitySelectorValues.CompanyTreeView}"
              label="${translateFn('Company tree view')}"
            ></gz-radio>
            <gz-radio
              value="${AccessibilitySelectorValues.FiltersOrSmartViews}"
              label="${translateFn('Filters or smart views')}"
            ></gz-radio>
            <gz-radio
              value="${AccessibilitySelectorValues.EndpointDetails}"
              label="${translateFn('Endpoint details')}"
            ></gz-radio>
            <gz-radio
              value="${AccessibilitySelectorValues.CriticalIssues}"
              label="${translateFn('Critical issues')}"
            ></gz-radio>
            <gz-radio
              value="${AccessibilitySelectorValues.RemediationActions}"
              label="${translateFn('Remediation actions')}"
            ></gz-radio>
            <gz-radio
              value="${AccessibilitySelectorValues.ReportsOrCsvFiles}"
              label="${translateFn('Reports or CSV files')}"
            ></gz-radio>
            <gz-radio
              value="${AccessibilitySelectorValues.Other}"
              label="${translateFn('Other. Please specify:')}"
            ></gz-radio>
          </gz-radio-group>
        </gz-form-field>

        <gz-form-field class="accessibility feedback-field mb-3 -mt-2" id="accessibilityFeedbackOneText" disabled>
          <gz-textarea
            gz-form-input
            rows="4"
            maxChars="${this.TEXTAREA_MAX_LENGTH}"
            placeholder="${translateFn('Type here')}"
          ></gz-textarea>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-text fontSize="14" class="mb-1">
          ${translateFn(
            '7. After testing the new design, what interface elements do you find helpful and what do you find unclear? Write down your suggestions:'
          )}
        </gz-text>

        <gz-form-field class="accessibility feedback-field mb-2" id="accessibilityFeedbackTwo">
          <gz-textarea
            gz-form-input
            rows="4"
            maxChars="${this.TEXTAREA_MAX_LENGTH}"
            placeholder="${translateFn('Type here')}"
          ></gz-textarea>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-network-feedback-form': GzNetworkFeedbackFormWebComponent;
  }
}
